import { Eye, Ghost, Lock } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import type { Child } from "../lib/types";
import { Fold } from "../ui/Collapsible";
import {
  CameraBlurSwitch,
  SchoolResourcesSection,
  StudentPortalSection,
  TurnInSection,
} from "./ChildSettings";
import { useParent } from "./ParentContext";
import { planHas, serverOff } from "./Plans";

/** Everything a parent can show or hide for a child (#454), grouped the way the child page is. Must match the server. */
export const CHILD_FEATURE_GROUPS: {
  label: string;
  items: { key: string; label: string; hint: string; plan?: boolean }[];
}[] = [
  {
    label: "Collaboration",
    items: [
      {
        key: "live_view",
        label: "Live view",
        hint: "Watch the screen and camera, hear and talk",
        plan: true,
      },
      {
        key: "interact",
        label: "Interact",
        hint: "Control the mouse and keyboard from live view",
        plan: true,
      },
      {
        key: "whiteboard",
        label: "Whiteboard",
        hint: "The shared whiteboard",
        plan: true,
      },
      {
        key: "chat",
        label: "Chat",
        hint: "Messages with each other",
        plan: true,
      },
    ],
  },
  {
    label: "Learning Hub",
    items: [
      {
        key: "planner",
        label: "Planner & calendar",
        hint: "Assignments, due dates and the calendar",
      },
      {
        key: "lesson_plans",
        label: "Lesson plans",
        hint: "Lesson plans in the planner",
        plan: true,
      },
      {
        key: "journal",
        label: "Journal",
        hint: "The homeschool journal",
        plan: true,
      },
      {
        key: "grade_book",
        label: "Grade book",
        hint: "Grades, categories and the grading scale",
        plan: true,
      },
      {
        key: "report_cards",
        label: "Report cards",
        hint: "Report cards and progress reports",
        plan: true,
      },
      {
        key: "attendance",
        label: "Attendance",
        hint: "Hours, estimates and attendance reports",
        plan: true,
      },
      {
        key: "reading_log",
        label: "Reading log",
        hint: "Books read and sign-off",
        plan: true,
      },
    ],
  },
  {
    label: "For the student",
    items: [
      {
        key: "student_portal",
        label: "Student Portal",
        hint: "Their own sign-in on the web",
        plan: true,
      },
      {
        key: "schedule",
        label: "Schedule (their view)",
        hint: "Today’s school hours in School Resources and the portal. School time still follows the schedule.",
      },
      // Separate switches since #625 (they used to be one).
      {
        key: "help",
        label: "Ask for help",
        hint: "Pinging you for help",
      },
      {
        key: "done",
        label: "Done for today",
        hint: "Saying they’ve finished; you decide whether school is over",
      },
      {
        key: "app_blocking",
        label: "App blocking",
        hint: "Blocking categories and single apps (blocking games stays available)",
        plan: true,
      },
    ],
  },
];

/** The features that belong to the whole family rather than one child (#469: only the server switch covers these). */
export const FAMILY_FEATURES: { key: string; label: string; hint: string }[] = [
  { key: "todo", label: "To-do list", hint: "The parents’ to-do list on Home" },
  {
    key: "reports",
    label: "Activity reports",
    hint: "Reports, website history exports and report emails",
  },
  {
    key: "api",
    label: "API & webhooks",
    hint: "Personal API keys and webhooks",
  },
];

/**
 * Child → Student → Features (#454): show only what this child uses. A virtual-school child might not need grades or
 * the planner. Hidden features disappear from your pages, the Student Portal and School Resources on their computer.
 * Nothing is deleted; showing a feature again brings everything back. What the family plan includes still applies.
 */
export function ChildFeatures({ child }: { child: Child }) {
  const { children, replaceChild, toast } = useParent();
  const [busy, setBusy] = useState(false);
  const hidden = child.hiddenFeatures ?? [];

  async function save(id: string, next: string[]) {
    replaceChild(
      await api.patch<Child>(`/api/children/${id}`, { hiddenFeatures: next }),
    );
  }
  async function toggle(key: string, show: boolean) {
    setBusy(true);
    try {
      await save(
        child.id,
        show ? hidden.filter((k) => k !== key) : [...hidden, key],
      );
    } catch (e) {
      toast({ text: (e as Error).message, tone: "alert" });
    } finally {
      setBusy(false);
    }
  }
  async function applyToAll() {
    const others = children.filter((c) => c.id !== child.id);
    if (
      !others.length ||
      !confirm(
        `Show and hide the same features for ${others.map((c) => c.name).join(", ")}?`,
      )
    )
      return;
    setBusy(true);
    try {
      for (const c of others) await save(c.id, hidden);
      toast({
        text: `Done. Everyone now sees the same features as ${child.name}.`,
        tone: "good",
      });
    } catch (e) {
      toast({ text: (e as Error).message, tone: "alert" });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {/* Hide School Resources first (#833): the biggest choice for the child's computer. */}
      <SchoolResourcesSection child={child} />
      {/* Ghost mode (#928): watch the school screen without the banner. Only matters with live view on. */}
      <GhostModeCard
        child={child}
        liveViewOn={planHas(child, "live_view") && !hidden.includes("live_view")}
      />
      <section className="card stack">
        <h2 className="with-icon">
          <Eye size={18} /> Features for {child.name}
        </h2>
        <p className="muted small">
          Turn off what {child.name} doesn’t use. It’s hidden from your pages,
          the Student Portal and School Resources on {child.name}’s computer.
          Nothing is deleted: turn it back on any time and everything is still
          there.
        </p>
        {/* A feature an admin turned off for everyone (#469) isn't listed at all. */}
        {CHILD_FEATURE_GROUPS.map((g) => ({
          ...g,
          items: g.items.filter((f) => !serverOff(child, f.key)),
        }))
          .filter((g) => g.items.length)
          .map((g) => (
            <fieldset key={g.label} className="stack plain-fieldset">
              <legend>{g.label}</legend>
              <div className="feature-checks">
                {g.items.map((f) => {
                  const inPlan = !f.plan || planHas(child, f.key);
                  return (
                    <label
                      key={f.key}
                      className={`check ${inPlan ? "" : "muted"}`}
                    >
                      <input
                        type="checkbox"
                        checked={inPlan && !hidden.includes(f.key)}
                        disabled={busy || !inPlan}
                        onChange={(e) => void toggle(f.key, e.target.checked)}
                      />
                      <span>
                        {f.label}
                        {!inPlan && (
                          <span className="tag">
                            <Lock size={11} /> Not in your plan
                          </span>
                        )}
                        <span className="muted small block">{f.hint}</span>
                      </span>
                    </label>
                  );
                })}
                {g.label === "Collaboration" && (
                  <CameraBlurSwitch
                    child={child}
                    liveViewOn={
                      planHas(child, "live_view") &&
                      !hidden.includes("live_view")
                    }
                  />
                )}
              </div>
            </fieldset>
          ))}
        {children.length > 1 && (
          <div>
            <button
              className="btn small"
              disabled={busy}
              onClick={() => void applyToAll()}
            >
              Use these for all my children
            </button>
          </div>
        )}
      </section>
      {/* The child's settings (#833), moved here from the Profile tab. */}
      <TurnInSection child={child} />
      <StudentPortalSection child={child} />
    </>
  );
}

/**
 * Ghost mode (#928): watch the child's school screen live without the amber "a parent is watching" banner, so a parent
 * can confirm the child is on task. Screen only — the camera and microphone always show the banner. Off by default; an
 * admin can turn it off for the whole family, and it only matters while live view is on.
 */
function GhostModeCard({ child, liveViewOn }: { child: Child; liveViewOn: boolean }) {
  const { parent, replaceChild, toast } = useParent();
  const [busy, setBusy] = useState(false);
  if (parent.ghostMode && !parent.ghostMode.allowed) return null; // an admin switched it off for everyone
  const on = child.ghostMode ?? false;
  async function set(next: boolean) {
    setBusy(true);
    try {
      replaceChild(await api.patch<Child>(`/api/children/${child.id}`, { ghostMode: next }));
    } catch (e) {
      toast({ text: (e as Error).message, tone: "alert" });
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="card stack">
      <h2 className="with-icon">
        <Ghost size={18} /> When you’re watching {child.name}’s screen
      </h2>
      <label className={`check ${liveViewOn ? "" : "muted"}`}>
        <input
          type="checkbox"
          checked={liveViewOn && on}
          disabled={busy || !liveViewOn}
          onChange={(e) => void set(e.target.checked)}
        />
        <span>
          Ghost mode — watch without the banner
          <span className="muted small block">
            Normally {child.name}’s computer shows a banner the whole time you watch live. With this on, you can check
            their screen without the banner, so you see what’s really happening. The camera and microphone always show
            the banner.
          </span>
        </span>
      </label>
      {!liveViewOn && (
        <p className="muted small">Turn on Live view below to use ghost mode.</p>
      )}
      {liveViewOn && on && (
        <p className="notice small">
          While ghost mode is on, {child.name} isn’t told when you watch their screen. You’ll see a reminder on your own
          screen so it stays clear to you.
        </p>
      )}
    </section>
  );
}

/**
 * Settings → Features for your children (#458): the same switches as each child's Features tab, for every child at
 * once. A switch is on when every child shows the feature, off when none does, and "mixed" (with who has it) when they
 * differ. Each child's own tab still works; new children start with what's chosen here.
 */
export function FamilyFeatures() {
  const { children, replaceChild, toast } = useParent();
  const [busy, setBusy] = useState(false);
  if (!children.length)
    return (
      <p className="muted small">
        Add a child first. Their features can be chosen here or on each child’s
        page.
      </p>
    );
  const sample = children[0]!;

  /** "4 of 6 on", and how many are on for only some children. */
  function groupSummary(items: { key: string; plan?: boolean }[]) {
    let on = 0;
    let mixed = 0;
    for (const f of items) {
      if (f.plan && !planHas(sample, f.key)) continue;
      const showing = children.filter(
        (c) => !c.hiddenFeatures?.includes(f.key),
      ).length;
      if (showing === children.length) on++;
      else if (showing > 0) mixed++;
    }
    return `${on} of ${items.length} on${mixed ? ` · ${mixed} mixed` : ""}`;
  }

  async function set(key: string, show: boolean) {
    setBusy(true);
    try {
      const r = await api.put<{ children: Child[] }>("/api/family/features", {
        feature: key,
        show,
      });
      r.children.forEach(replaceChild);
    } catch (e) {
      toast({ text: (e as Error).message, tone: "alert" });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack">
      <p className="muted small">
        Turn a feature off for all your children at once. Each child’s own page
        (open a child → Student → Features) can still change it for just that
        child, and children you add later start with what you choose here.
        Nothing is deleted.
      </p>
      {/* A feature an admin turned off for everyone (#469) isn't listed at all. Each group is a fold (#601). */}
      {CHILD_FEATURE_GROUPS.map((g) => ({
        ...g,
        items: g.items.filter((f) => !serverOff(sample, f.key)),
      }))
        .filter((g) => g.items.length)
        .map((g) => (
          <Fold key={g.label} title={g.label} summary={groupSummary(g.items)}>
            <div className="feature-checks" role="group" aria-label={g.label}>
              {g.items.map((f) => {
                const inPlan = !f.plan || planHas(sample, f.key);
                const showing = children.filter(
                  (c) => !c.hiddenFeatures?.includes(f.key),
                );
                const mixed =
                  inPlan &&
                  showing.length > 0 &&
                  showing.length < children.length;
                return (
                  <label
                    key={f.key}
                    className={`check ${inPlan ? "" : "muted"}`}
                  >
                    <input
                      type="checkbox"
                      checked={inPlan && showing.length === children.length}
                      ref={(el) => {
                        if (el) el.indeterminate = mixed;
                      }}
                      disabled={busy || !inPlan}
                      onChange={() =>
                        void set(f.key, showing.length < children.length)
                      }
                      aria-label={`${f.label}${mixed ? " (mixed)" : ""}`}
                    />
                    <span>
                      {f.label}
                      {!inPlan && (
                        <span className="tag">
                          <Lock size={11} /> Not in your plan
                        </span>
                      )}
                      <span className="muted small block">
                        {mixed
                          ? `On for ${showing.map((c) => c.name).join(", ")} only`
                          : f.hint}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </Fold>
        ))}
      <ChildThemeSwitch />
    </div>
  );
}

/**
 * Settings → Features (#819): whether the children can change their theme. One choice for the family; it covers School
 * Resources on their computers and the Student Portal, which share the theme.
 */
function ChildThemeSwitch() {
  const { toast } = useParent();
  const [on, setOn] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api
      .get<{ childThemeChange: boolean }>("/api/family/child-theme")
      .then((r) => setOn(r.childThemeChange))
      .catch(() => setOn(null));
  }, []);
  if (on === null) return null; // an older server, or still loading

  async function change(next: boolean) {
    setBusy(true);
    try {
      const r = await api.put<{ childThemeChange: boolean }>(
        "/api/family/child-theme",
        { childThemeChange: next },
      );
      setOn(r.childThemeChange);
    } catch (e) {
      toast({ text: (e as Error).message, tone: "alert" });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Fold title="Theme" summary={on ? "Children choose" : "You choose"}>
      <div className="feature-checks" role="group" aria-label="Theme">
        <label className="check">
          <input
            type="checkbox"
            checked={on}
            disabled={busy}
            onChange={() => void change(!on)}
          />
          <span>
            Children can change their theme
            <span className="muted small block">
              The colors of School Resources on their computers and of the
              Student Portal. Off, they keep the theme they have.
            </span>
          </span>
        </label>
      </div>
    </Fold>
  );
}
